import React, { useEffect } from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import GavelIcon from '@mui/icons-material/Gavel';
import PetsIcon from '@mui/icons-material/Pets';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import CampaignIcon from '@mui/icons-material/Campaign';
import PublicIcon from '@mui/icons-material/Public';

const PhilanthropyPage: React.FC = () => {
  useEffect(() => {
    document.title = 'Philanthropy | Kevyn Franco';
  }, []);

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          backgroundColor: '#dc143c',
          color: 'white',
          py: { xs: 8, md: 12 },
          minHeight: '50vh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: '900px', mx: 'auto' }}>
            <FavoriteIcon sx={{ fontSize: { xs: 60, md: 80 }, mb: 3 }} />
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 900,
                fontFamily: '"Impact", sans-serif',
                mb: 3,
                textTransform: 'uppercase',
              }}
            >
              GIVING BACK
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                lineHeight: 1.6,
                maxWidth: '700px',
                mx: 'auto',
              }}
            >
              Using our platform to make a positive impact on the world
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Mission Statement */}
      <Box
        sx={{
          backgroundColor: 'white',
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 900,
              fontFamily: '"Impact", sans-serif',
              mb: 4,
              textTransform: 'uppercase',
            }}
          >
            OUR COMMITMENT
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: '1.125rem', md: '1.25rem' },
              lineHeight: 1.8,
              mb: 3,
              maxWidth: '900px',
            }}
          >
            I do my best to use my platform to elevate organizations that are giving
            back to animals, our communities, and the environment. I'm committed
            to donating a significant percentage of our revenue to impactful
            organizations.
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: '1.125rem', md: '1.25rem' },
              lineHeight: 1.8,
              mb: 4,
              maxWidth: '900px',
            }}
          >
            Through strategic partnerships and direct contributions, we support
            causes that align with our values and make a measurable difference
            in the world.
          </Typography>
        </Container>
      </Box>

      {/* Organizations We Support */}
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 900,
              fontFamily: '"Impact", sans-serif',
              mb: 6,
              textTransform: 'uppercase',
              textAlign: 'center',
            }}
          >
            ORGANIZATIONS WE SUPPORT
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 4,
            }}
          >
            {[
              {
                name: 'Equal Justice Initiative',
                description: 'Working to end mass incarceration and excessive punishment',
                Icon: GavelIcon,
              },
              {
                name: 'Animal Equality',
                description: 'Protecting farmed animals through investigations and advocacy',
                Icon: PetsIcon,
              },
              {
                name: 'Farm Sanctuary',
                description: 'Rescue, education, and advocacy for farm animals',
                Icon: AgricultureIcon,
              },
              {
                name: 'The Humane League',
                description: 'Ending the abuse of animals raised for food',
                Icon: VolunteerActivismIcon,
              },
              {
                name: 'Campaign Zero',
                description: 'Research-based policy solutions to end police violence',
                Icon: CampaignIcon,
              },
              {
                name: 'Environmental Defense Fund',
                description: 'Finding practical solutions to environmental challenges',
                Icon: PublicIcon,
              },
            ].map((org, index) => {
              const IconComponent = org.Icon;
              return (
              <Box
                key={index}
                sx={{
                  backgroundColor: 'white',
                  p: 4,
                  borderRadius: 0,
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: '#dc143c',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3,
                  }}
                >
                  <IconComponent
                    sx={{
                      fontSize: 40,
                      color: 'white',
                    }}
                  />
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: '1.5rem', md: '1.75rem' },
                    fontWeight: 900,
                    fontFamily: '"Arial Black", sans-serif',
                    mb: 2,
                  }}
                >
                  {org.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1rem',
                    lineHeight: 1.6,
                    color: '#666',
                  }}
                >
                  {org.description}
                </Typography>
              </Box>
              );
            })}
          </Box>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box
        sx={{
          backgroundColor: '#1a1a1a',
          color: 'white',
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '3rem' },
                fontWeight: 900,
                fontFamily: '"Impact", sans-serif',
                mb: 4,
                textTransform: 'uppercase',
              }}
            >
              JOIN US IN MAKING A DIFFERENCE
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: '1.125rem', md: '1.25rem' },
                lineHeight: 1.8,
                mb: 4,
              }}
            >
              Whether through donations, volunteering, or spreading awareness,
              every action counts. Together, we can create positive change.
            </Typography>

            <Button
              variant="contained"
              sx={{
                backgroundColor: '#dc143c',
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 700,
                '&:hover': {
                  backgroundColor: '#b01030',
                },
              }}
            >
              Learn More
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PhilanthropyPage;